import type React from 'react';
import { useState, useEffect, useCallback, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useAssetsData } from '@makemydeal/dr-dash-components';
import { kebabCase } from '@makemydeal/dr-common-utils';
import { dealerSelectors, featureToggleSelectors } from '@makemydeal/dr-shared-store';
import { searchAndSortOptions } from '@makemydeal/dr-shared-ui-utils';
import type { CommitFieldIfChanged, LienholderFormData } from '../../../types';
import {
    LIENHOLDER_MAX_LENGTH,
    LIENHOLDER_FORM_FIELDS,
    LIENHOLDER_SEARCGH_MIN_LENGTH,
    PHONE_NUMBER_SANITIZE_REGEX
} from './constants';
import { LIEN_HOLDER } from '../../../constants';
import { logNewRelicPageAction } from '@makemydeal/dr-activities-common';

import * as tradeInActionCreators from '@makemydeal/dr-dash-store/actions/tradeInActionCreators';
import * as offerReduxSelectors from '@makemydeal/dr-dash-store/selectors/offerRedux';
import * as vehicleSelectors from '@makemydeal/dr-dash-store/selectors/vehicleSelectors';

import type { AutocompleteOptions } from '@makemydeal/dr-shared-types';
import type { LendersFormatted } from '@makemydeal/dr-dash-types';
import { useSingleTradeInFormProvider } from '../MultiTradeIn/TradeInFormProvider';

import type { LienholderFieldProps } from './lienholderFormTypes';

export const useLienholderField = ({
    lienholderData,
    setLienholderData,
    isEditDisabled = false,
    softSaveLienHolderData,
    isOutsideLienholder = false,
    isTradeInCompleted,
    renderDropdownPortal = false
}: LienholderFieldProps) => {
    const dispatch = useDispatch();
    const { getLenders, lenders } = useAssetsData();
    const [filteredLenders, setFilteredLenders] = useState<LendersFormatted[]>([]);
    const [isSearchingProvider, setIsSearchingProvider] = useState(false);
    const [isLenderSearchOptionSelected, setIsLenderSearchOptionSelected] = useState(false);
    const previousLienHolderValues = useRef<LienholderFormData>(lienholderData);

    const sessionId = useSelector(offerReduxSelectors.getSessionId);
    const dealXgId = useSelector(offerReduxSelectors.getDealXgId);
    const dealXgVersion = useSelector(offerReduxSelectors.getDealXgVersion);
    const dealerId = useSelector(dealerSelectors.getDealerId);
    const vin = useSelector(vehicleSelectors.getVehicleVin);

    const { tradeInValidator: lienholderValidator, tradeInFormErrors: lienholderFormErrors } = useSingleTradeInFormProvider();
    const formErrors = lienholderFormErrors.lienholder;
    const isFieldDisabled = isEditDisabled || !isTradeInCompleted;

    useEffect(() => {
        lienholderValidator.setValue('lienholder.name', lienholderData.lienholder || '', { validate: true, clearErrors: false });
        lienholderValidator.validatePath('lienholder.name');
    }, [lienholderData.lienholder, lienholderValidator]);

    const logNRAction = useCallback(
        (name: string, searchTerm: string, selection?: string) => {
            logNewRelicPageAction(name, {
                sessionId,
                dealXgId,
                dealXgVersion,
                dealerId,
                vin,
                searchTerm,
                ...(selection && { selection })
            });
        },
        [dealXgId, dealXgVersion, dealerId, sessionId, vin]
    );

    const commitFieldIfChanged: CommitFieldIfChanged = (fieldName: keyof LienholderFormData) => () => {
        if (previousLienHolderValues.current[fieldName] !== lienholderData[fieldName]) {
            previousLienHolderValues.current = {
                ...previousLienHolderValues.current,
                [fieldName]: lienholderData[fieldName]
            } as LienholderFormData;
            softSaveLienHolderData?.();
        }
    };

    const handleSoftSaveOnEnterKey = useCallback(
        (commitChanges: () => void) => (event: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement | HTMLDivElement>) => {
            if (event.key === 'Enter') {
                commitChanges();
            }
        },
        []
    );

    useEffect(() => {
        if (!lenders.length) {
            getLenders();
        }
    }, []);

    useEffect(() => {
        setFilteredLenders(lenders);
    }, [lenders]);

    useEffect(() => {
        if (isLenderSearchOptionSelected) {
            softSaveLienHolderData?.();
            setIsLenderSearchOptionSelected(false);
        }
    }, [isLenderSearchOptionSelected]);

    const onSearchProvider = (input: string) => {
        setIsSearchingProvider(true);

        onChange({ target: { value: input }, isValid: input.length <= LIENHOLDER_MAX_LENGTH });

        // filter provider list when input length is greater than 3
        if (input.length >= LIENHOLDER_SEARCGH_MIN_LENGTH) {
            const filteredData = searchAndSortOptions(input, lenders);

            setFilteredLenders(filteredData);
            dispatch(tradeInActionCreators.searchTradeinLienholderName());

            // log to NewRelic
            if (filteredData.length > 0) {
                logNRAction('MV:LienholderForm - Found match', input);
            } else {
                logNRAction('MV:LienholderForm - No match', input);
            }
        } else {
            setFilteredLenders([]);
        }
        setIsSearchingProvider(false);
    };

    const changeLenderInformation = (e: AutocompleteOptions) => {
        const lender = e as LendersFormatted;
        const currentSearchTerm = lienholderData.lienholder;

        // clear filtered list
        setFilteredLenders([]);
        // set all the fields
        const newData = {
            ...lienholderData,
            lienholder: lender.label,
            lienholderPhoneNumber: lender.Phone ? lender.Phone.replace(PHONE_NUMBER_SANITIZE_REGEX, '') : '',
            lienholderAddress: {
                ...lienholderData.lienholderAddress,
                address: lender.Address,
                postalCode: lender.ZIP,
                stateProvince: lender.State,
                city: lender.City
            }
        };
        setLienholderData(newData);

        lienholderValidator.setValues(
            {
                lienholder: {
                    name: lender.label || '',
                    streetAddress: lender.Address || '',
                    city: lender.City || '',
                    state: lender.State || '',
                    zip: lender.ZIP || ''
                }
            },
            { mode: 'merge', validate: true }
        );
        lienholderValidator.validatePath('lienholder.name');

        dispatch(tradeInActionCreators.autocompleteTradeinLienholderName());
        previousLienHolderValues.current = {
            ...newData,
            lienholderAddress: { ...newData.lienholderAddress }
        };
        setTimeout(() => setIsLenderSearchOptionSelected(true));

        // log to NewRelic
        logNRAction('MV:LienholderForm - Provider selected', currentSearchTerm, lender.label);
    };

    const onChange = useCallback(
        (e: any) => {
            const value = e.target.value;
            setLienholderData({
                ...lienholderData,
                lienholder: value
            });
            lienholderValidator.setValue('lienholder.name', value, { validate: true, clearErrors: false });
            lienholderValidator.validatePath('lienholder.name');
        },
        [lienholderData, setLienholderData, lienholderValidator]
    );

    const sharedProps = {
        label: LIEN_HOLDER,
        name: kebabCase(LIEN_HOLDER),
        'data-testid': isOutsideLienholder ? 'outside-lienholder' : 'lienholder',
        value: lienholderData.lienholder,
        onKeyDown: handleSoftSaveOnEnterKey(commitFieldIfChanged(LIENHOLDER_FORM_FIELDS.LIENHOLDER)),
        onBlur: commitFieldIfChanged(LIENHOLDER_FORM_FIELDS.LIENHOLDER),
        disabled: isFieldDisabled,
        size: 'sm' as const,
        hasError: formErrors.name.hasError,
        errorMessage: formErrors.name.errorMessage,
        selectOnFocusSelector: featureToggleSelectors.isAutoSelectContentOnSlideOutsEnabledAfterBootstrap
    };

    return {
        hasLenders: lenders.length > 0,
        autocompleteProps: {
            ...sharedProps,
            menuOptions: filteredLenders,
            isLoading: isSearchingProvider,
            onChange: (value: string) => {
                lienholderValidator.setValue('lienholder.name', value, { validate: true, clearErrors: false });
                lienholderValidator.validatePath('lienholder.name');
                setLienholderData({ ...lienholderData, lienholder: value });
            },
            onSearch: onSearchProvider,
            onSelect: changeLenderInformation,
            onFocus: () => dispatch(tradeInActionCreators.selectTradeinLienholderName()),
            renderDropdownPortal
        },
        textInputProps: { ...sharedProps, onChange }
    };
};
